const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const scrollBehavior = () =>
  reducedMotion.matches || document.documentElement.dataset.motion === 'off' ? 'auto' : 'smooth';

class SliderComponent extends HTMLElement {
  connectedCallback() {
    if (this.track) {
      this.observe();
      return;
    }
    this.track = this.own('[data-slider-track]');
    if (!this.track) return;
    this.prevButton = this.own('[data-slider-prev]');
    this.nextButton = this.own('[data-slider-next]');
    this.dotsContainer = this.own('[data-slider-dots]');
    this.counter = this.own('[data-slider-counter]');
    this.current = 0;
    this.target = null;
    this.state = {};
    this.geometry = { slides: [], offsets: [], pad: 0, maxScroll: 0, pageCount: 0, stride: 1, visible: 1 };

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('scrollend', () => (this.target = null), { passive: true });
    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('keydown', (event) => this.onKeydown(event));
    if (!this.track.hasAttribute('tabindex')) this.track.setAttribute('tabindex', '0');

    this.initDrag();
    this.scheduleRefresh = () => {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    };
    this.resizeObserver = new ResizeObserver(this.scheduleRefresh);
    this.mutationObserver = new MutationObserver(this.scheduleRefresh);
    if (window.Shopify?.designMode) {
      this.onBlockSelect = (event) => {
        const slide = this.slides.find((item) => item === event.target || item.contains(event.target));
        if (slide) this.goTo(this.slides.indexOf(slide));
      };
    }
    this.observe();
  }

  observe() {
    this.resizeObserver.observe(this.track);
    this.mutationObserver.observe(this.track, { childList: true });
    if (this.onBlockSelect) document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.refresh();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.refreshFrame);
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  own(selector) {
    return [...this.querySelectorAll(selector)].find((element) => element.closest('slider-component') === this) || null;
  }

  get slides() {
    return this.geometry.slides;
  }

  get pageCount() {
    return this.geometry.pageCount;
  }

  // All layout reads happen here (resize / mutation), so scroll frames only read scrollLeft.
  measure() {
    const track = this.track;
    const style = getComputedStyle(track);
    const slides = [...track.children].filter((child) => child.getClientRects().length);
    const pad = parseFloat(style.scrollPaddingInlineStart) || 0;
    const padEnd = parseFloat(style.scrollPaddingInlineEnd) || 0;
    const gap = parseFloat(style.columnGap) || 0;
    const viewport = Math.max(1, track.clientWidth - pad - padEnd);
    const trackLeft = track.getBoundingClientRect().left - track.scrollLeft;
    const offsets = slides.map((slide) => slide.getBoundingClientRect().left - trackLeft);
    const width = slides[0]?.getBoundingClientRect().width || viewport;
    const stride = Math.max(1, width + gap);
    const visible = Math.max(1, Math.floor((viewport + gap + 2) / stride));
    this.geometry = {
      slides,
      offsets,
      pad,
      stride,
      visible,
      maxScroll: Math.max(0, track.scrollWidth - track.clientWidth),
      pageCount: slides.length ? Math.max(1, slides.length - visible + 1) : 0,
    };
  }

  refresh() {
    this.measure();
    this.renderDots();
    this.state = {};
    this.update();
  }

  renderDots() {
    if (!this.dotsContainer) return;
    const count = this.pageCount;
    if (this.dotsContainer.children.length === count) return;
    const label = this.dataset.dotLabel || 'Slide';
    this.dotsContainer.replaceChildren(
      ...Array.from({ length: count }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider-dot';
        dot.setAttribute('aria-label', `${label} ${index + 1}`);
        dot.addEventListener('click', () => this.goTo(index));
        return dot;
      })
    );
    this.dotsContainer.hidden = count < 2;
  }

  onScroll() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.update);
  }

  positionOf(index) {
    const { offsets, pad, maxScroll } = this.geometry;
    return Math.min(maxScroll, Math.max(0, (offsets[index] ?? 0) - pad));
  }

  update() {
    const { offsets, pad, maxScroll, pageCount } = this.geometry;
    if (!offsets.length) return;
    const scroll = Math.abs(this.track.scrollLeft);
    let index = 0;
    let best = Infinity;
    for (let i = 0; i < offsets.length; i += 1) {
      const distance = Math.abs(offsets[i] - pad - scroll);
      if (distance < best) {
        best = distance;
        index = i;
      }
    }
    if (scroll >= maxScroll - 2) index = Math.max(index, pageCount - 1);
    index = Math.min(index, Math.max(0, pageCount - 1));
    const atStart = scroll <= 2;
    const atEnd = scroll >= maxScroll - 2;
    const scrollable = maxScroll > 2;
    const previous = this.state;
    this.current = index;
    if (previous.index === index && previous.atStart === atStart && previous.atEnd === atEnd && previous.scrollable === scrollable) return;
    this.state = { index, atStart, atEnd, scrollable };

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
    if (previous.scrollable !== scrollable) this.classList.toggle('is-scrollable', scrollable);
    if (previous.index === index) return;
    if (this.dotsContainer) {
      [...this.dotsContainer.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
    if (this.counter) this.counter.textContent = `${index + 1} / ${pageCount}`;
    this.dispatchEvent(new CustomEvent('slider:change', { detail: { index }, bubbles: true }));
  }

  step(direction) {
    const { visible, pageCount } = this.geometry;
    const size = this.dataset.stepPage !== undefined ? visible : 1;
    const from = this.target ?? this.current;
    this.goTo(Math.max(0, Math.min(from + direction * size, Math.max(0, pageCount - 1))));
  }

  goTo(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const next = Math.max(0, Math.min(index, slides.length - 1));
    this.target = next;
    this.track.scrollTo({ left: this.positionOf(next), behavior: scrollBehavior() });
  }

  onKeydown(event) {
    if (event.target !== this.track) return;
    const actions = {
      ArrowRight: () => this.step(1),
      ArrowLeft: () => this.step(-1),
      Home: () => this.goTo(0),
      End: () => this.goTo(this.slides.length - 1),
    };
    if (!actions[event.key]) return;
    event.preventDefault();
    actions[event.key]();
  }

  initDrag() {
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let moved = false;
    let pointerId = null;
    let dragFrame = 0;
    let pendingScroll = 0;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('[data-slider-track]') !== this.track) return;
      if (event.target.closest('button, input, select, textarea')) return;
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      lastTime = event.timeStamp;
      velocity = 0;
      startScroll = this.track.scrollLeft;
      moved = false;
    });

    this.track.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 6) return;
      if (!moved) {
        moved = true;
        this.target = null;
        this.track.setPointerCapture(pointerId);
        this.track.classList.add('is-dragging');
      }
      const elapsed = Math.max(1, event.timeStamp - lastTime);
      velocity = (event.clientX - lastX) / elapsed;
      lastX = event.clientX;
      lastTime = event.timeStamp;
      pendingScroll = startScroll - delta;
      if (!dragFrame) {
        dragFrame = requestAnimationFrame(() => {
          dragFrame = 0;
          this.track.scrollLeft = pendingScroll;
        });
      }
    });

    const end = (event) => {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      if (!moved) return;
      cancelAnimationFrame(dragFrame);
      dragFrame = 0;
      this.track.scrollLeft = pendingScroll;
      this.track.classList.remove('is-dragging');
      this.update();
      const fling = Math.abs(velocity) > 0.35 ? Math.sign(-velocity) : 0;
      const nearest = this.current;
      const drift = Math.sign(this.track.scrollLeft - this.positionOf(nearest));
      const next = fling && drift === fling ? nearest + fling : nearest;
      this.goTo(Math.max(0, Math.min(next, Math.max(0, this.pageCount - 1))));
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);

    this.track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );
    this.track.addEventListener('dragstart', (event) => {
      if (event.target.closest('img, a')) event.preventDefault();
    });
  }
}

if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);
